import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useReducedMotion} from 'framer-motion'
import {Box} from '@chakra-ui/react3'
import DisImage from '../dis-image'
import Loading from '../loading-container'
import {useProductDisplayImages} from '../../../hooks/use-product-display-images'
import logger from '../../../utils/logger-instance'
import useEffectEvent from '../../../hooks/use-effect-event'
import {createPropsComparator} from '../../../utils/react-memo-helpers'

/**
 * Individual image record structure
 * @typedef {Object} ImageRecord
 * @property {string} src - Image source URL
 * @property {number} zIndex - Stack position (z-index)
 * @property {'visible'|'entering'|'exiting'} state - Animation state
 * @property {boolean} loaded - Whether image has loaded
 */

/**
 * StackableImage component stacks multiple images on top of each other with smooth transitions.
 * Designed for product configurators where images need to be layered and changed dynamically.
 * Only changed images transition when the stack is updated.
 *
 * When no images are provided or some images are missing, displays a fallback image from the
 * product's mainview image group (first image).
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Array<string>} props.images - Array of image URLs to stack (max 5)
 * @param {string} props.alt - Accessibility label describing the selected products (required)
 * @param {Object} [props.product] - Product object to extract fallback image from mainview image group
 * @param {string} [props.aspectRatio='1 / 1'] - Aspect ratio (e.g., '1 / 1', '16 / 9')
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy for images
 * @returns {JSX.Element|null} Stacked images container
 *
 * @accessibility
 * Respects user's `prefers-reduced-motion` system setting via two mechanisms:
 * 1. JavaScript state management: When reduced motion is enabled, exiting images are removed
 *    immediately without animation state, and entering images skip directly to 'visible' state,
 *    providing instant visibility changes without any animation delays
 * 2. CSS media query: Uses Chakra v3's `_motionSafe` conditional which only applies scale/fade
 *    animations when @media (prefers-reduced-motion: no-preference) matches. When motion is
 *    reduced, this CSS is not applied, so images rely solely on the JavaScript optimization
 * This combined approach ensures WCAG 2.1 SC 2.3.3 compliance (Animation from Interactions, Level AAA)
 *
 * @example
 * // Basic usage
 * <StackableImage
 *   images={[
 *     'https://example.com/base.jpg',
 *     'https://example.com/layer1.png',
 *     'https://example.com/layer2.png'
 *   ]}
 *   alt="Product configuration"
 * />
 *
 * @example
 * // With fallback from product
 * <StackableImage
 *   images={['image1.jpg', 'image2.jpg']}
 *   product={product}
 *   alt="Product configuration"
 * />
 */
const StackableImage = ({
    alt,
    aspectRatio = '1 / 1',
    images,
    loading = 'lazy',
    product,
    widths,
    children,
    ...rest
}) => {
    const reducedMotion = useReducedMotion()
    const validImages = useMemo(() => images?.slice(0, 5) || [], [images])
    const isInitialMount = useRef(true)

    // Get fallback image from product's mainview image group
    const productImages = useProductDisplayImages(product, 'mainview', product?.variationValues, 1)
    const fallbackImageSrc = useMemo(
        () => productImages?.[0]?.disBaseLink || productImages?.[0]?.link,
        [productImages]
    )
    useEffect(() => {
        if (!fallbackImageSrc && product) {
            logger.error('StackableImage: No fallback image available for product', {product})
        }
    }, [fallbackImageSrc, product])

    // log when no valid images
    const onValidImagesChange = useEffectEvent((validImages) => {
        if (validImages?.length) {
            return
        }
        logger.warn(
            `StackableImage: No valid images provided. Using fallback from mainview image group.`,
            {
                productId: product?.id,
                productName: product?.name,
                fallbackSrc: fallbackImageSrc
            }
        )
    })
    useEffect(() => {
        onValidImagesChange(validImages)
    }, [validImages, onValidImagesChange])

    const [imageRecords, setImageRecords] = useState(() =>
        validImages.map((src, index) => ({
            src,
            zIndex: index,
            state: 'visible',
            loaded: false
        }))
    )

    const handleImageLoad = useCallback((src) => {
        setImageRecords((prev) =>
            prev.map((r) => (r.src === src && !r.loaded ? {...r, loaded: true, error: false} : r))
        )
    }, [])
    const onImageLoadError = useEffectEvent((src) => {
        logger.warn('StackableImage: Failed to load image', {
            failedImageSrc: src,
            productId: product?.id,
            productName: product?.name,
            fallbackSrc: fallbackImageSrc
        })
    })
    const handleImageError = useCallback(
        (src) => {
            setImageRecords((prev) =>
                prev.map((r) =>
                    r.src === src && !r.loaded ? {...r, loaded: true, error: true} : r
                )
            )
            onImageLoadError(src)
        },
        [onImageLoadError]
    )

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }

        setImageRecords((prev) => {
            const prevSrcs = prev.map((r) => r.src)
            const removedSrcs = prevSrcs.filter((src) => !validImages.includes(src))

            const next = validImages.map((src, index) => {
                const existing = prev.find((r) => r.src === src)
                if (existing) {
                    // If the image is being re-added while it's exiting, reset its state
                    if (existing.state === 'exiting') {
                        return {
                            src,
                            zIndex: index,
                            state: reducedMotion ? 'visible' : 'entering',
                            loaded: existing.loaded, // Preserve loaded state
                            error: existing.error // Preserve error state
                        }
                    }
                    // Otherwise just update z-index, keeping existing state
                    return {...existing, zIndex: index}
                }
                // New image
                return {
                    src,
                    zIndex: index,
                    // Reduced motion: instant visibility, no animation states
                    state: reducedMotion ? 'visible' : 'entering',
                    loaded: false
                }
            })

            // Reduced motion: remove old images immediately without exit animation
            if (!reducedMotion) {
                removedSrcs.forEach((src) => {
                    const existing = prev.find((r) => r.src === src)
                    if (existing?.state !== 'exiting') {
                        next.push({...existing, state: 'exiting'})
                    }
                })
            }

            return next
        })
    }, [validImages, reducedMotion])

    const handleAnimationEnd = useCallback((src, state) => {
        if (state === 'entering') {
            setImageRecords((prev) =>
                prev.map((r) =>
                    r.src === src && r.state === 'entering' ? {...r, state: 'visible'} : r
                )
            )
        } else if (state === 'exiting') {
            setImageRecords((prev) => prev.filter((r) => !(r.src === src && r.state === 'exiting')))
        }
    }, [])

    const isLoading = useMemo(
        () =>
            imageRecords.some(
                (r) =>
                    !r.loaded &&
                    (r.state === 'entering' || (r.state === 'visible' && reducedMotion))
            ),
        [imageRecords, reducedMotion]
    )

    // Determine if we should use fallback image
    const shouldUseFallback = useMemo(
        () =>
            // no images
            !validImages.length ||
            // or some of the new images failed to load
            imageRecords.some((r) => r.loaded && r.error && r.state !== 'exiting'),
        [validImages, imageRecords]
    )

    return (
        <Loading.Root position="relative" width="100%" height="100%" aspectRatio={aspectRatio}>
            <Loading.Status>
                {/* Loading spinner */}
                {isLoading && !shouldUseFallback && (
                    <Loading.Spinner data-testid="stackable-image-spinner" zIndex="overlay" />
                )}
            </Loading.Status>
            <Loading.Content asChild>
                <Box
                    position="absolute"
                    inset="0"
                    overflow="hidden"
                    data-testid="stackable-image-container"
                    role="img"
                    aria-label={alt}
                    {...rest}
                >
                    {shouldUseFallback ? (
                        fallbackImageSrc ? (
                            <DisImage
                                src={fallbackImageSrc}
                                alt=""
                                loading={loading}
                                widths={widths}
                                position="absolute"
                                inset={0}
                                width="100%"
                                height="100%"
                                objectFit="cover"
                                objectPosition="center"
                                data-testid="fallback-image"
                            />
                        ) : null
                    ) : (
                        <>
                            {imageRecords.map((record) => {
                                const isEnteringNotLoaded =
                                    record.state === 'entering' && !record.loaded
                                const isExiting = record.state === 'exiting'
                                const isOpen =
                                    record.state === 'visible' ||
                                    (record.state === 'entering' && record.loaded)
                                const shouldAnimate =
                                    record.state !== 'visible' && (isExiting || record.loaded)

                                return (
                                    <DisImage
                                        key={record.src}
                                        src={record.src}
                                        alt=""
                                        loading={loading}
                                        widths={widths}
                                        position="absolute"
                                        inset={0}
                                        width="100%"
                                        height="100%"
                                        objectFit="cover"
                                        objectPosition="center"
                                        data-testid={`stackable-image-${record.zIndex}`}
                                        data-state={isOpen ? 'open' : 'closed'}
                                        style={{zIndex: record.zIndex}}
                                        {...(isEnteringNotLoaded && {
                                            opacity: 0,
                                            visibility: 'hidden'
                                        })}
                                        // CSS animations only apply when prefers-reduced-motion: no-preference
                                        // Reduced motion relies on JS state optimization above (instant visible state)
                                        _motionSafe={
                                            shouldAnimate
                                                ? {
                                                      animationName: {
                                                          _open: 'scale-in, fade-in',
                                                          _closed: 'scale-out, fade-out'
                                                      },
                                                      animationDuration: 'slowest',
                                                      animationFillMode: 'both',
                                                      animationDelay: {_open: 'slowest'}
                                                  }
                                                : {}
                                        }
                                        {...(!record.loaded && {
                                            onLoad: () => handleImageLoad(record.src),
                                            onError: () => handleImageError(record.src),
                                            imgRef: (e) => {
                                                if (e?.complete) {
                                                    if (e.naturalWidth && e.naturalHeight) {
                                                        handleImageLoad(record.src)
                                                    } else {
                                                        handleImageError(record.src)
                                                    }
                                                }
                                            }
                                        })}
                                        onAnimationEnd={() =>
                                            handleAnimationEnd(record.src, record.state)
                                        }
                                    />
                                )
                            })}
                            {children}
                        </>
                    )}
                </Box>
            </Loading.Content>
        </Loading.Root>
    )
}

if (process.env.NODE_ENV === 'development') {
    StackableImage.displayName = 'StackableImage'
}

StackableImage.propTypes = {
    children: PropTypes.node,
    images: PropTypes.arrayOf(PropTypes.string),
    alt: PropTypes.string.isRequired,
    product: PropTypes.object,
    aspectRatio: PropTypes.string,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    widths: PropTypes.object
}

/**
 * Props comparison function for React.memo
 * Two-pass approach: shallow compare all props first (cheap), then deep compare only if needed (expensive)
 *
 * @private
 * @internal
 * @description Exported only for testing purposes. Do not use directly in application code.
 * @param {Object} prevProps - Previous props
 * @param {Object} nextProps - Next props
 * @returns {boolean} True if props are equal (skip re-render), false if different (re-render)
 */
export const propsAreEqual = createPropsComparator({
    deepCompareProps: ['images', 'widths']
})

export default React.memo(StackableImage, propsAreEqual)
